/**
 * StatusPhotoStrip — DEV-1079, Chunk A.
 *
 * Reusable photo strip with two variants:
 *
 *   mode='read'  - gallery for the StatusReader and StatusSection.
 *                  Thumbnails with static captions. Click a thumb to
 *                  open a fullscreen lightbox.
 *
 *   mode='write' - thumbnails with remove × + caption inputs + an add
 *                  tile. Upload via the existing fileStorage pipeline.
 *                  Soft cap (default 6) hides the add tile when reached.
 *
 * Photos are referenced by storage_key (migration 174). The strip
 * doesn't know about status_guid or the parent record — caller passes
 * photos in and gets the updated array back via onChange.
 *
 * Strings localized via `pages:miniProject.status.photoStrip.*`
 * (Chunk D Phase 1 sweep, 2026-05-22).
 */

import React, { useCallback, useEffect, useRef, useState } from "react";
import { useAppTranslation } from "../../i18n/useAppTranslation";
import { uploadFile } from "../../api/fileStorage";
import { mediaSrc } from "../../api/mediaUrl";
import { compressImage } from "../../lib/compressImage";
import styles from "./StatusPhotoStrip.module.css";

/* ── Types ─────────────────────────────────────────────────────── */

export interface StatusPhotoItem {
  /** fileStorage key (or empty if not yet uploaded — caller should
   *  not pass these in; only the upload flow inside this component
   *  produces them). */
  storageKey: string;
  caption?: string | null;
  position: number;
}

interface Props {
  mode: "read" | "write";
  photos: StatusPhotoItem[];
  /** Required in write mode. Fires with the full new photos array. */
  onChange?: (photos: StatusPhotoItem[]) => void;
  /** Soft cap. Default 6. */
  maxPhotos?: number;
  /** DEV-1075 Phase 7.3a — fileStorage category for uploads.
   *  Defaults to the status-photos bucket to preserve pre-7.3a
   *  behavior. Goal-edit-tray consumers pass 'miniproject-goal-photos'
   *  so the two photo sets stay separately namespaced in storage. */
  uploadBucket?: string;
  /** DEV-1075 Phase 7.3a — slug prefix for the generated upload
   *  filename. Defaults to 'status-photo'. Goal-edit-tray consumers
   *  pass 'goal-photo' for grep-friendly storage keys. Purely
   *  cosmetic; uploads work either way. */
  uploadFilePrefix?: string;
}

const DEFAULT_MAX_PHOTOS = 6;
const DEFAULT_UPLOAD_BUCKET = "miniproject-status-photos";
const DEFAULT_UPLOAD_PREFIX = "status-photo";
const MAX_RAW_FILE_BYTES = 10 * 1024 * 1024; // 10 MB raw input cap

/* ── Component ─────────────────────────────────────────────────── */

const StatusPhotoStrip: React.FC<Props> = ({
  mode,
  photos,
  onChange,
  maxPhotos = DEFAULT_MAX_PHOTOS,
  uploadBucket = DEFAULT_UPLOAD_BUCKET,
  uploadFilePrefix = DEFAULT_UPLOAD_PREFIX,
}) => {
  const { t } = useAppTranslation("pages");
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);

  // ── Lightbox keyboard handling ──
  useEffect(() => {
    if (lightboxIndex === null) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setLightboxIndex(null);
    };
    document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  }, [lightboxIndex]);

  // ── Write mode: file picker handler ──
  const handlePickFile = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const handleFileChange = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      // Reset the input so picking the same file twice still triggers change.
      if (e.target) e.target.value = "";
      if (!file) return;
      if (!file.type.startsWith("image/")) {
        setError(t("miniProject.status.photoStrip.error.notImage"));
        return;
      }
      if (file.size > MAX_RAW_FILE_BYTES) {
        setError(t("miniProject.status.photoStrip.error.tooLarge", { mb: MAX_RAW_FILE_BYTES / 1024 / 1024 }));
        return;
      }
      if (photos.length >= maxPhotos) {
        setError(t("miniProject.status.photoStrip.error.maxReached", { max: maxPhotos }));
        return;
      }
      setError(null);
      setUploading(true);
      try {
        // Compress before upload. 1600px max dim, ~500KB target. Matches
        // the pattern used by SectionBodyEditor for inline images. The
        // uploadBucket + uploadFilePrefix props below let goal-edit-tray
        // consumers (DEV-1075 Phase 7.3a) namespace storage cleanly
        // without forking this component.
        const { blob } = await compressImage(file, {
          maxDimension: 1600,
          quality: 0.82,
          maxBytes: 500_000,
        });
        const slug = `${uploadFilePrefix}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}.jpg`;
        const result = await uploadFile(blob, uploadBucket, undefined, slug);
        const next: StatusPhotoItem[] = [
          ...photos,
          {
            storageKey: result.key,
            caption: null,
            position: photos.length,
          },
        ];
        onChange?.(next);
      } catch (err) {
        console.error("[StatusPhotoStrip] upload failed:", err);
        setError(err instanceof Error ? err.message : t("miniProject.status.photoStrip.error.uploadFailed"));
      } finally {
        setUploading(false);
      }
    },
    [photos, maxPhotos, onChange, t, uploadBucket, uploadFilePrefix],
  );

  // ── Write mode: remove ──
  const handleRemove = useCallback(
    (index: number) => {
      if (!onChange) return;
      const next = photos
        .filter((_, i) => i !== index)
        .map((p, i) => ({ ...p, position: i }));
      onChange(next);
    },
    [photos, onChange],
  );

  // ── Write mode: caption edit ──
  const handleCaptionChange = useCallback(
    (index: number, value: string) => {
      if (!onChange) return;
      const next = photos.map((p, i) =>
        i === index ? { ...p, caption: value } : p,
      );
      onChange(next);
    },
    [photos, onChange],
  );

  // ── Read mode: lightbox open ──
  const handleThumbClick = useCallback((index: number) => {
    setLightboxIndex(index);
  }, []);

  // Sort by position before render so the array order matches display order.
  const sortedPhotos = [...photos].sort((a, b) => a.position - b.position);
  const atCap = photos.length >= maxPhotos;

  // ── Render ──
  return (
    <div className={styles.wrap}>
      {sortedPhotos.length === 0 && mode === "read" && (
        <div className={styles.emptyRead}>{t("miniProject.status.photoStrip.emptyRead")}</div>
      )}

      {/* Photo strip — fixed-width tiles in a single row. Each existing
          photo holds a slot; the Add affordance is the trailing slot in
          write mode (until the cap is hit). The row scrolls horizontally
          rather than stretching tiles when more photos are added. */}
      {(sortedPhotos.length > 0 || (mode === "write" && !atCap)) && (
        <div className={styles.grid}>
          {sortedPhotos.map((p, i) => (
            <div key={p.storageKey} className={styles.cell}>
              {/* thumbWrap is the positioning context for the remove ×
                  overlay. Earlier the × was nested INSIDE the disabled
                  thumb <button> which blocked pointer events on its
                  descendants in write mode (Chrome behavior). Now the
                  × is a sibling <button>, properly clickable. */}
              <div className={styles.thumbWrap}>
                <button
                  type="button"
                  className={styles.thumb}
                  onClick={mode === "read" ? () => handleThumbClick(i) : undefined}
                  aria-label={mode === "read"
                    ? t("miniProject.status.photoStrip.openPhoto")
                    : t("miniProject.status.photoStrip.photo")}
                  disabled={mode === "write"}
                >
                  <img
                    src={mediaSrc(p.storageKey) ?? undefined}
                    alt={p.caption || ""}
                    className={styles.thumbImg}
                    loading="lazy"
                  />
                </button>
                {mode === "write" && (
                  <button
                    type="button"
                    className={styles.removeBtn}
                    aria-label={t("miniProject.status.photoStrip.removePhoto")}
                    onClick={() => handleRemove(i)}
                  >
                    ×
                  </button>
                )}
              </div>
              {mode === "write" ? (
                <input
                  type="text"
                  className={styles.captionInput}
                  value={p.caption || ""}
                  onChange={(e) => handleCaptionChange(i, e.target.value)}
                  placeholder={t("miniProject.status.photoStrip.captionPlaceholder")}
                />
              ) : (
                p.caption && <div className={styles.captionRead}>{p.caption}</div>
              )}
            </div>
          ))}

          {mode === "write" && !atCap && (
            <div className={styles.cell}>
              <button
                type="button"
                className={styles.addTile}
                onClick={handlePickFile}
                disabled={uploading}
                aria-label={t("miniProject.status.photoStrip.addPhoto")}
              >
                <span className={styles.addPlus} aria-hidden="true">+</span>
                <span className={styles.addLabel}>
                  {uploading
                    ? t("miniProject.status.photoStrip.uploading")
                    : t("miniProject.status.photoStrip.addPhoto")}
                </span>
              </button>
            </div>
          )}
        </div>
      )}

      {mode === "write" && (
        <>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            style={{ display: "none" }}
            onChange={handleFileChange}
          />
          {atCap && (
            <div className={styles.capHint}>
              {t("miniProject.status.photoStrip.capHint", { count: photos.length, max: maxPhotos })}
            </div>
          )}
          {error && <div className={styles.errorMsg}>{error}</div>}
        </>
      )}

      {/* Lightbox — read mode only, opens on thumb click. */}
      {lightboxIndex !== null && sortedPhotos[lightboxIndex] && (
        <div
          className={styles.lightbox}
          onClick={() => setLightboxIndex(null)}
          role="dialog"
          aria-label={t("miniProject.status.photoStrip.lightboxAria")}
        >
          <button
            type="button"
            className={styles.lightboxClose}
            onClick={(e) => {
              e.stopPropagation();
              setLightboxIndex(null);
            }}
            aria-label={t("miniProject.status.photoStrip.closeLightbox")}
          >
            ×
          </button>
          <img
            src={mediaSrc(sortedPhotos[lightboxIndex].storageKey) ?? undefined}
            alt={sortedPhotos[lightboxIndex].caption || ""}
            className={styles.lightboxImg}
            onClick={(e) => e.stopPropagation()}
          />
          {sortedPhotos[lightboxIndex].caption && (
            <div className={styles.lightboxCaption}>
              {sortedPhotos[lightboxIndex].caption}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default StatusPhotoStrip;
